/**
 * The single chokepoint for the credit-card sign convention in the UI.
 *
 * Storage convention (see docs/plans/archive/2026-04-22-balance-tracking-design.md):
 * balances are signed cents with liability negative. A credit card with
 * $1,200 owed is stored as -120000; an overpaid card with a $50 credit is
 * stored as +5000. Net worth = sum(all stored balances).
 *
 * Display convention: credit cards are framed as "amount owed" — positive
 * when in debt, negative when the card carries a credit.
 *
 * Every component that renders or accepts a credit-card balance MUST go
 * through these helpers instead of negating/abs-ing inline. Four separate
 * sign bugs (migration, record modal, Plaid sync, display) all came from
 * sites re-deriving this transform by hand.
 */

/**
 * Flip a sign without producing negative zero: `-0` survives arithmetic and
 * formats as "-$0.00", so a card sitting at exactly zero would read as owing
 * a negative amount.
 */
const flip = (cents: number): number => (cents === 0 ? 0 : -cents);

/**
 * Stored balance → display value. For credit cards, returns the "amount owed"
 * (positive = debt, negative = credit). Identity for other accounts.
 */
export const toDisplayBalance = (
  storedCents: number,
  isCreditCard: boolean,
): number => (isCreditCard ? flip(storedCents) : storedCents);

/**
 * User-entered display value → stored sign convention. For credit cards, the
 * user enters the amount owed (or a negative value for an overpayment /
 * statement credit). Exact inverse of toDisplayBalance.
 */
export const toStoredBalance = (
  displayCents: number,
  isCreditCard: boolean,
): number => (isCreditCard ? flip(displayCents) : displayCents);

/**
 * Label + non-negative display amount for a headline balance figure. Credit
 * cards read "Amount owed $X" when in debt and "Credit balance $X" when
 * overpaid, so a credit is never mislabeled as debt.
 */
export const describeBalance = (
  storedCents: number,
  isCreditCard: boolean,
): {label: string; amount: number} => {
  if (!isCreditCard) {
    return {label: 'Current balance', amount: storedCents};
  }
  return storedCents > 0
    ? {label: 'Credit balance', amount: storedCents}
    : {label: 'Amount owed', amount: flip(storedCents)};
};
